import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"

/**
 * WeeklyReviewSection - Aggregated weekly summary across all coaches
 *
 * Shows:
 * - Completion rates per coach
 * - Top wins of the week
 * - Cross-coach patterns detected
 * - Blockers to address
 * - Suggested experiment for next week
 */

export default function WeeklyReviewSection() {
  const { data, isLoading, refetch } = trpc.coachDashboard.getWeeklyReview.useQuery({})

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">📊 Weekly Review</h2>
        <div className="border-2 border-border rounded-lg p-6 animate-pulse">
          <div className="h-6 bg-muted rounded mb-4 w-1/3"></div>
          <div className="space-y-3">
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-5/6"></div>
            <div className="h-4 bg-muted rounded w-4/6"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!data || data.coachSummaries.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">📊 Weekly Review</h2>
        <div className="border-2 border-border rounded-lg p-6 text-center bg-muted/30">
          <p className="text-sm text-muted-foreground">
            Weekly review will appear here once you have active coaches with completed weeks.
          </p>
        </div>
      </div>
    )
  }

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    return `${startDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${endDate.toLocaleDateString(
      "en-US",
      { month: "short", day: "numeric" },
    )}`
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">
          📊 Your Week — {formatDateRange(data.weekStart, data.weekEnd)}
        </h2>
        <Button onClick={() => refetch()} variant="ghost" size="sm" className="text-xs">
          Refresh →
        </Button>
      </div>

      <div className="border-2 border-border rounded-lg p-6 space-y-6 bg-background">
        {/* Completion Overview */}
        <div className="space-y-3">
          <h3 className="font-bold text-foreground">🎯 Completion Overview</h3>
          <div className="space-y-2">
            {data.coachSummaries.map((summary) => {
              const statusEmoji =
                summary.status === "ahead"
                  ? "✅"
                  : summary.status === "on-track"
                    ? "✅"
                    : summary.status === "behind"
                      ? "⚠️"
                      : "📝"

              return (
                <div key={summary.coachId} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{summary.coachIcon}</span>
                    <span className="font-medium text-sm text-foreground">{summary.coachName}:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {summary.completedActions}/{summary.totalActions} actions
                    </span>
                    <span className="text-lg">{statusEmoji}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Top Wins */}
        {data.topWins.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-bold text-foreground">🏆 Top Wins This Week</h3>
            <ul className="space-y-2">
              {data.topWins.map((win, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span>{win.coachIcon}</span>
                  <span className="text-foreground">{win.insight}</span>
                  <span className="text-muted-foreground">({win.coachName})</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Cross-Coach Patterns */}
        {data.crossCoachInsights.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-bold text-foreground">🔬 Patterns Discovered</h3>
            <div className="space-y-2">
              {data.crossCoachInsights.map((insight, idx) => (
                <div key={idx} className="p-3 rounded border-2 border-primary/30 bg-primary/5">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">⚡</span>
                    <div className="flex-1">
                      <p className="text-sm text-foreground">{insight.insight}</p>
                      <p className="text-xs text-muted-foreground mt-1">Confidence: {insight.confidence}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Blockers */}
        {data.blockers.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-bold text-foreground">⚠️ Blockers</h3>
            <ul className="space-y-2">
              {data.blockers.map((blocker, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <span>{blocker.coachIcon}</span>
                  <span className="text-foreground">{blocker.blocker}</span>
                  <span className="text-muted-foreground">({blocker.coachName})</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Suggested Experiment */}
        {data.suggestedExperiment && (
          <div className="space-y-3">
            <h3 className="font-bold text-foreground">🧪 This Week&apos;s Experiment</h3>
            <div className="p-3 rounded border-2 border-primary/30 bg-primary/5">
              <p className="text-sm text-foreground">{data.suggestedExperiment}</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {data.topWins.length === 0 &&
          data.crossCoachInsights.length === 0 &&
          data.blockers.length === 0 &&
          !data.suggestedExperiment && (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground">
                Complete a few more weeks to see insights, patterns, and suggestions here!
              </p>
            </div>
          )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-border">
          <Button variant="outline" size="sm" className="flex-1">
            💬 Chat About This Week
          </Button>
          <Button variant="outline" size="sm" className="flex-1">
            📅 Plan Next Week
          </Button>
        </div>
      </div>
    </div>
  )
}
